import { useState, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { getComponentMargin, isRichTextEmpty } from '@/utils/builderUtils';
import beTwMerge from '@/utils/beTwMerge';
import useStore from '@/store/useStore';
import { dataVirtual } from '@/utils/constants';
import { IDataShowroom } from '@/components/BuilderIO/common/CommonFields';
import Title from '@/components/Title/Title';
import RichText from '@/components/RichText/RichText';
import { GetHeadingLevel } from '@/components/BuilderIO/common/StoreLocator/utils';
import useCoordinates from '@/hooks/useCoordinates';
import StoreLocatorSearchBar from './StoreLocatorSearchBar';

const StoreAccordion = dynamic(() => import('@/components/StoreLocator/StoreAccordion/StoreAccordion'), { ssr: false });
const StoreLocatorDetails = dynamic(() => import('./StoreLocatorDetails'));
const StoreLocatorCards = dynamic(() => import('./StoreLocatorCards'));

export interface ITitle {
  content: string;
  titleHTMLTag?: 'h1' | 'h2';
}

export interface IStoreLocator {
  title: ITitle;
  description?: string;
  margin: boolean;
}

const VIEWS = {
  storesNearYou: 1,
  allStores: 2,
};

export default function StoreLocator({ title, description, margin }: IStoreLocator) {
  const setShowroomModalInfo = useStore(state => state.setAppointmentBookingInformation);
  const setIsAppointmentBookingModalVisible = useStore(state => state.setIsAppointmentBookingModalVisible);
  const [activeView, setActiveView] = useState(VIEWS.storesNearYou);

  const setNearShowrooms = useStore(state => state.setNearShowrooms);
  const user = useStore(state => state.user);
  const filteredShowrooms = useStore(state => state.filteredShowrooms);
  const loadingFirstTime = useStore(state => state.loadingFirstTime);
  const setGeolocation = useStore(state => state.setGeolocation);

  const [showroomIndex, setShowroomIndex] = useState(0);
  const [showroomSelected, setShowroomSelected] = useState<IDataShowroom | any | null>(null);
  const [showroomVirtual, setShowroomVirtual] = useState<IDataShowroom | any | null>(null);
  const [hasError, setHasError] = useState(false);
  const [errorSearchMessage, setErrorSearchMessage] = useState('');
  const [titleLocationsNear, settitleLocationsNear] = useState('locations near you');
  const [builderShowroomsCopy, setBuilderShowroomsCopy] = useState<any>([]);
  const [isMobile, setIsMobile] = useState(false);

  const isDescriptionEmpty = isRichTextEmpty(description);
  const searchContainer = useRef<any>(null);

  const { getCoordinates } = useCoordinates();

  const timezones = {
    PST: 'PT', // Pacific Standard Time
    PDT: 'PT', // Pacific Daylight Time
    EST: 'ET', // Eastern Standard Time
    EDT: 'ET', // Eastern Daylight Time
    CST: 'CT', // Central Standard Time
    CDT: 'CT', // Central Daylight Time
    MST: 'MT', // Mountain Standard Time
    MDT: 'MT', // Mountain Daylight Time
    AKST: 'AKT', // Alaska Standard Time
    AKDT: 'AKT', // Alaska Daylight Time
    HST: 'HST', // Hawaii-Aleutian Standard Time (Hawaii does not observe DST)
  };

  function calculateInitialCoords() {
    if (!user.ipLatitude || !user.ipLongitude) {
      setGeolocation({});
    }
  }

  // This should be made into a hook
  useEffect(() => {
    function handleResize() {
      if (typeof window === 'undefined') {
        setIsMobile(false);
      } else {
        setIsMobile(window?.innerWidth < 768);
      }
    }
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    calculateInitialCoords();
    setShowroomVirtual(dataVirtual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateNearShowrooms = useCallback(() => {
    if (user.ipLatitude && user.ipLongitude) {
      setNearShowrooms(user.ipLatitude, user.ipLongitude, null, '');
    }
  }, [user.ipLatitude, user.ipLongitude, setNearShowrooms]);

  useEffect(() => {
    updateNearShowrooms();
  }, [updateNearShowrooms]);

  useEffect(() => {
    if (filteredShowrooms && filteredShowrooms.length > 0 && builderShowroomsCopy.length === 0) {
      setBuilderShowroomsCopy([...filteredShowrooms]);

      const virtual = filteredShowrooms.find((item: IDataShowroom) => item.setsterLocationId === 'Virtual Appointment');
      if (virtual) {
        setShowroomVirtual(virtual);
      }

      if (!showroomSelected) {
        setShowroomSelected(filteredShowrooms[0]);
      }
    } else if (filteredShowrooms && filteredShowrooms.length === 0 && !loadingFirstTime && !showroomSelected) {
      setShowroomSelected(dataVirtual);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredShowrooms, showroomSelected, builderShowroomsCopy, loadingFirstTime]);

  useEffect(() => {
    if (filteredShowrooms && filteredShowrooms.length > 0) {
      setShowroomSelected(filteredShowrooms[0]);
      setBuilderShowroomsCopy([...filteredShowrooms]);
      setShowroomIndex(0);
    }
  }, [filteredShowrooms]);

  const checkViewChange = () => {
    if (activeView === VIEWS.allStores) {
      setActiveView(VIEWS.storesNearYou);
    }
  };

  const handleInputSearch = async (searchInput: string) => {
    (window as any).dataLayer.push({
      event: activeView === VIEWS.storesNearYou ? 'showroom_locator' : 'view_all_showrooms',
      showroom_action: 'search',
      showroom_location: 'undefined',
      showroom_page_section: 'search bar',
    });

    setHasError(false);
    setErrorSearchMessage('');
    const newTitle = searchInput ? `locations near ${searchInput}` : 'locations near you';

    if (!searchInput) {
      let latitude = user.ipLatitude;
      let longitude = user.ipLongitude;

      if (!latitude || !longitude) {
        ({ latitude, longitude } = await setGeolocation({}));
      }

      await setNearShowrooms(latitude, longitude, null, '');
      settitleLocationsNear(newTitle);
      checkViewChange();

      return;
    }

    const coords = await getCoordinates(searchInput);

    let filterList: IDataShowroom[] = [];

    if (coords) {
      filterList = await setNearShowrooms(coords?.lat, coords?.lng, coords?.state, searchInput);
      checkViewChange();
    } else {
      setErrorSearchMessage('Invalid City, State, or Zip Code.');
      setHasError(true);
      return;
    }

    if (!filterList || filterList?.length === 0) {
      setBuilderShowroomsCopy([]);
      setShowroomSelected(showroomVirtual);
      setShowroomIndex(0);
      settitleLocationsNear(newTitle);
      return;
    }

    const virtualIndex = filterList.findIndex(location => location.displayName1 === 'Virtual Appointment');
    const updatedList =
      virtualIndex !== -1
        ? [...filterList.slice(0, virtualIndex), ...filterList.slice(virtualIndex + 1)]
        : [...filterList];

    setShowroomSelected(filterList?.length > 0 ? filterList[0] : showroomVirtual);
    setShowroomIndex(0);
    setBuilderShowroomsCopy(updatedList?.length > 0 ? [...updatedList, showroomVirtual] : [...updatedList]);
    settitleLocationsNear(newTitle);
  };

  const bookAppointment = (showroom: IDataShowroom) => {
    if (showroom.displayName1 === 'Virtual Appointment') {
      const virtualInfo = {
        modalName: 'Virtual Appointment',
        displayName1: 'Virtual Appointment',
        displayName2: '',
        addresLine1: '',
        setsterLocationId: 'Virtual Appointment',
        addressLine2: '',
        id: showroom.id,
        timezoneId: '553',
        timezoneName: '',
        timezoneAbreviation: 'PT',
        city: '',
        state: '',
        zipCode: '',
      };
      setShowroomModalInfo('activePage', 2);
      setShowroomModalInfo('prevSelectedShowroomStore', virtualInfo);
      setShowroomModalInfo('selectedShowroomStore', virtualInfo);
    } else {
      const showroomInfo = {
        modalName: `${showroom?.displayName1}${showroom?.displayName2 ? ` - ${showroom?.displayName2}` : ''}`,
        displayName1: showroom?.displayName1 || '',
        displayName2: showroom?.displayName2 || '',
        addresLine1: showroom?.addressLine1 || '',
        addressLine2: showroom?.addressLine2 || '',
        id: showroom.id,
        timezoneName: showroom?.timezoneName || '',
        timezoneId: showroom?.timezoneId || '',
        timezoneAbreviation: timezones[showroom?.timezoneAbreviation || ''] || '',
        setsterLocationId: showroom?.setsterLocationId || '',
        city: showroom?.city || '',
        state: showroom?.state || '',
        zipCode: showroom?.zipCode || '',
        latitude: showroom?.latitude || '',
        longitude: showroom?.longitude || '',
      };

      setShowroomModalInfo('activePage', 2);
      setShowroomModalInfo('prevSelectedShowroomStore', showroomInfo);
      setShowroomModalInfo('selectedShowroomStore', showroomInfo);
      setShowroomModalInfo('selectedTimezoneId', '0');
    }
    setIsAppointmentBookingModalVisible(true);
  };

  const changeView = (view: number) => {
    setActiveView(view);
  };

  const handleViewAllLocationCTA = (notFoundResults = false) => {
    setActiveView(VIEWS.allStores);
    (window as any).dataLayer.push({
      event: 'showroom_locator',
      showroom_action: 'view all locations',
      showroom_location: 'undefined',
      showroom_page_section: builderShowroomsCopy.length === 0 ? 'no results found' : 'under results',
    });

    const topNavBarRect: DOMRect = document.querySelector('.mobile-header-banner-height')?.getBoundingClientRect()!;
    if (
      searchContainer.current &&
      (!notFoundResults || window.scrollY > 0 || isMobile) &&
      topNavBarRect?.height === 46
    ) {
      searchContainer?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div
      ref={searchContainer}
      className={beTwMerge(
        'tw-min-h-[786.91px] lg:tw-min-h-[614px] tw-flex tw-flex-col tw-px-[1rem] tw-gap-[2rem] tw-self-center min-[1200px]:tw-px-0 lg:tw-mx-auto tw-max-w-[1142px] min-[1440px]:tw-gap-[2.5rem]',
        getComponentMargin(margin),
        'tw-w-full'
      )}
    >
      {/* Title and Description */}
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-[8px] tw-w-full">
        <div className="tw-max-w-[900px]">
          {title?.content && (
            <Title
              component={title?.titleHTMLTag || 'h1'}
              weight="medium"
              className={beTwMerge('!tw-mb-0 !tw-leading-normal', !isDescriptionEmpty && '!tw-mb-[8px]')}
            >
              {title?.content}
            </Title>
          )}
          {!isDescriptionEmpty && (
            <RichText
              content={description || ''}
              className="tw-font-book tw-text-inherit tw-text-center tw-font-[350] [&>p]:tw-font-book [&>p]:tw-font-[350] [&>p]:!tw-leading-[21.86px]"
            />
          )}
        </div>
      </div>
      {/* Search bar and store cards container */}
      <div className="tw-flex tw-flex-col tw-w-full tw-gap-[24px] min-[1440px]:tw-gap-[2rem]">
        {/* Search Bar container */}
        <StoreLocatorSearchBar
          activeView={activeView}
          hasError={hasError}
          isMobile={isMobile}
          errorSearchMessage={errorSearchMessage}
          bookAppointment={bookAppointment}
          changeView={changeView}
          handleInputSearch={handleInputSearch}
        />

        {/* Error message */}
        {hasError && !isMobile && (
          <div className="tw-w-full tw-flex tw-flex-row tw-mt-[-26px] tw-mb-[-10px] min-[1440px]:tw-mt-[-36px] tw-font-book">
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-error">{errorSearchMessage}</p>
          </div>
        )}

        <div
          style={{ display: activeView === VIEWS.storesNearYou ? 'block' : 'none' }}
          className="tw-flex tw-flex-col tw-min-h-[643px] lg:tw-min-h-[386px]"
        >
          {isMobile && (
            <div className="tw-mb-[1rem] lg:tw-hidden">
              <GetHeadingLevel
                mainLevel={title?.titleHTMLTag || 'h1'}
                currentLevel={1}
                asClass
                styles="tw-font-base tw-mb-0 tw-text-left !tw-text-[1rem] tw-font-medium min-[768px]:tw-m-0 tw-capitalize lg:!tw-text-[18px]"
              >
                {titleLocationsNear}
              </GetHeadingLevel>
            </div>
          )}

          {!loadingFirstTime &&
            showroomSelected &&
            showroomSelected.displayName1 === 'Virtual Appointment' &&
            builderShowroomsCopy.length === 0 && (
              <p className="tw-mb-[1rem] tw-font-book lg:tw-hidden hover:tw-cursor-pointer">
                We&apos;re sorry, there are no Brilliant Earth stores within 250 miles. Please try another search or{' '}
                <a className="tw-underline" onClick={() => handleViewAllLocationCTA(true)} role="button" tabIndex={0}>
                  View All Locations
                </a>
                .
              </p>
            )}

          <div
            className={beTwMerge(
              'tw-flex tw-flex-col tw-gap-[8px] lg:tw-w-full lg:tw-flex-row lg:tw-gap-[2rem]',
              builderShowroomsCopy.length > 0 && isMobile && 'tw-mb-[24px]'
            )}
          >
            {/* Cards list */}
            <StoreLocatorCards
              isLoading={loadingFirstTime}
              builderShowroomsCopy={builderShowroomsCopy}
              isMobile={isMobile}
              showroomIndex={showroomIndex}
              title={title}
              titleLocationsNear={titleLocationsNear}
              setShowroomModalInfo={setShowroomModalInfo}
              bookAppointment={bookAppointment}
              setShowroomIndex={setShowroomIndex}
              setShowroomSelected={setShowroomSelected}
              handleViewAllLocationCTA={handleViewAllLocationCTA}
            />

            {/* Card INFO */}
            <StoreLocatorDetails
              isLoading={loadingFirstTime}
              showroomSelected={showroomSelected}
              builderShowroomsCopy={builderShowroomsCopy}
              isMobile={isMobile}
              bookAppointment={bookAppointment}
              showroomIndex={showroomIndex}
            />
          </div>
          {!loadingFirstTime && builderShowroomsCopy.length > 0 && (
            <a
              className="tw-mt-[8px] tw-block tw-cursor-pointer tw-underline tw-font-normal tw-leading-normal tw-text-center tw-capitalize lg:tw-hidden"
              onClick={() => handleViewAllLocationCTA()}
              role="presentation"
            >
              view all locations
            </a>
          )}
        </div>
        <div style={{ display: activeView !== VIEWS.storesNearYou ? 'block' : 'none' }}>
          <StoreAccordion titleHTMLTag={title?.titleHTMLTag || 'h1'} handleOpenAppointmentModal={bookAppointment} />
        </div>
      </div>
    </div>
  );
}
